import { Injectable, inject } from '@angular/core';
import {
  ConfirmationService,
  TableDataPayload,
  TableLazyLoad,
  ToastService,
} from '@web/shared/ui';
import { map, Observable, switchMap, tap } from 'rxjs';
import { CollectionRequestBuilder } from '@web/common';
import { blank, Guid, once, valueOrDefault } from '@web/utils';
import { WarehouseFileRequest } from '../api/requests/warehouse-file.request';
import { multimediaFromResponse } from '@web/shared/module/multimedia';
import { WarehouseReleaseCollectionItem } from '../models/warehouse-release-collection-item';
import { WarehouseReleaseCollectionFilter } from '../models/warehouse-release-collection-filter';
import { WarehouseReleaseApiService } from '../api/services/warehouse-release-api.service';
import {
  WarehouseReleaseFormData,
  WarehouseReleaseProductFormData,
} from '../models/warehouse-release-form-data';
import { WarehouseReleaseCreateRequest } from '../api/requests/warehouse-release-create.request';
import { WarehouseReleaseUpdateRequest } from '../api/requests/warehouse-release-update.request';
import { WarehouseReleaseProductRequest } from '../api/requests/warehouse-release-product.request';
import { WarehouseReleaseDetails } from '../models/warehouse-release-details';
import { WarehouseReleaseProduct } from '../models/warehouse-release-product';

@Injectable({
  providedIn: 'root',
})
export class WarehouseReleaseDataSourceService {
  private readonly warehouseReleaseApi = inject(WarehouseReleaseApiService);
  private readonly confirmation = inject(ConfirmationService);
  private readonly collectionRequestBuilder = inject(CollectionRequestBuilder);
  private readonly toast = inject(ToastService);

  public fetchCollection(
    lazyLoad: TableLazyLoad<WarehouseReleaseCollectionFilter>,
  ): Observable<TableDataPayload<WarehouseReleaseCollectionItem>> {
    return this.warehouseReleaseApi
      .fetchCollection(
        this.collectionRequestBuilder
          .page(lazyLoad.page)
          .pageSize(lazyLoad.rows)
          .sorts((builder) =>
            builder.fromTableSort(lazyLoad.sort).buildAsArray(),
          )
          .filters((builder) =>
            builder
              .fromTableFilter(lazyLoad.filter)
              .appendText((builder) =>
                builder.caseInsensitiveLikeFor('number').build(),
              )
              .appendText((builder) =>
                builder.caseInsensitiveLikeFor('status').build(),
              )
              .appendText((builder) =>
                builder.caseInsensitiveLikeFor('goodsNames').build(),
              )
              .appendDate((builder) => builder.rangeFor('releasedAt').build())
              .appendDate((builder) => builder.rangeFor('createdAt').build())
              .buildAsArray(),
          )
          .build(),
      )
      .pipe(
        map(
          (response) =>
            new TableDataPayload<WarehouseReleaseCollectionItem>(
              response.items.map(
                (item) =>
                  new WarehouseReleaseCollectionItem({
                    canBeApproved: item.contextOperations.canBeApproved,
                    canBeEdited: item.contextOperations.canBeEdited,
                    canBeViewed: item.contextOperations.canBeViewed,
                    createdAt: item.createdAt,
                    id: item.id,
                    number: item.number,
                    productNames: item.productNames,
                    releasedAt: item.releasedAt,
                    status: item.status,
                  }),
              ),
              response.pageSize,
              response.page,
              response.total,
            ),
        ),
      );
  }

  /* prettier-ignore */
  public fetchDetailsFormById(id: Guid): Observable<WarehouseReleaseFormData> {
    return this.warehouseReleaseApi.fetchDetailsById(id).pipe(
      switchMap((response) => this.warehouseReleaseApi
        .fetchProductCollection(
          id,
          this.collectionRequestBuilder.disablePagination().build(),
        )
        .pipe(
          map(
            ({ items }) =>
              new WarehouseReleaseFormData({
                releasedAt: response.releasedAt,
                products: items.map(
                  (item) =>
                    new WarehouseReleaseProductFormData({
                      id: item.id,
                      productId: item.good.id,
                      quantity: item.quantity,
                    }),
                ),
                comment: response.comment,
                fileIds: response.files.map((file) => file.id),
              }),
          ),
        )),
    );
  }

  public fetchDetailsById(id: Guid): Observable<WarehouseReleaseDetails> {
    return this.warehouseReleaseApi.fetchDetailsById(id).pipe(
      switchMap((response) =>
        this.warehouseReleaseApi
          .fetchProductCollection(
            id,
            this.collectionRequestBuilder.disablePagination().build(),
          )
          .pipe(
            map(
              ({ items }) =>
                new WarehouseReleaseDetails({
                  canBeApproved: response.contextOperations.canBeApproved,
                  canBeEdited: response.contextOperations.canBeEdited,
                  canBeViewed: response.contextOperations.canBeViewed,
                  comment: response.comment,
                  createdAt: response.createdAt,
                  id: response.id,
                  number: response.number,
                  releasedAt: response.releasedAt,
                  approvedAt: response.approvedAt,
                  status: response.status,
                  files: response.files.map((file) =>
                    multimediaFromResponse(file),
                  ),
                  products: items.map(
                    (item) =>
                      new WarehouseReleaseProduct({
                        brandName: item.good.brand.label,
                        productId: item.good.id,
                        productName: item.good.name,
                        createdAt: item.createdAt,
                        id: item.id,
                        quantity: item.quantity,
                        sku: item.good.sku,
                      }),
                  ),
                }),
            ),
          ),
      ),
    );
  }

  public approveById(id: Guid): Observable<void> {
    return new Observable<void>((subscriber) => {
      this.confirmation.show((builder) =>
        builder
          .title('Potwierdzenie')
          .message('Czy na pewno chcesz zatwierdzić wydanie zewnętrzne?')
          .accept(() => {
            this.warehouseReleaseApi
              .approveById(id)
              .pipe(
                once(),
                tap(() => {
                  this.toast.show((builder) =>
                    builder
                      .initSuccess(
                        'Wydanie zewnętrzne zostało zatwierdzone pomyślnie.',
                      )
                      .build(),
                  );
                }),
              )
              .subscribe(subscriber);
          })
          .build(),
      );
    });
  }

  public createByFormData(data: WarehouseReleaseFormData): Observable<void> {
    return this.warehouseReleaseApi
      .create(
        new WarehouseReleaseCreateRequest({
          comment: data.comment,
          releasedAt: data.releasedAt,
          products: data.products.map(
            (product) =>
              new WarehouseReleaseProductRequest({
                id: product.productId,
                quantity: product.quantity,
              }),
          ),
          files: valueOrDefault(data.fileIds, []).map(
            (id) => new WarehouseFileRequest(id),
          ),
        }),
      )
      .pipe(
        tap(() => {
          this.toast.show((builder) =>
            builder
              .initSuccess('Wydanie zewnętrznego zostało dodany pomyślnie.')
              .build(),
          );
        }),
        blank(),
      );
  }

  public updateByFormData(
    id: Guid,
    data: WarehouseReleaseFormData,
  ): Observable<void> {
    return this.warehouseReleaseApi
      .update(
        id,
        new WarehouseReleaseUpdateRequest({
          comment: data.comment,
          releasedAt: data.releasedAt,
          products: data.products.map(
            (product) =>
              new WarehouseReleaseProductRequest({
                id: product.productId,
                quantity: product.quantity,
              }),
          ),
          files: valueOrDefault(data.fileIds, []).map(
            (id) => new WarehouseFileRequest(id),
          ),
        }),
      )
      .pipe(
        tap(() => {
          this.toast.show((builder) =>
            builder
              .initSuccess(
                'Wydanie zewnętrzne zostało zaktualizowane pomyślnie.',
              )
              .build(),
          );
        }),
        blank(),
      );
  }
}
